import type { BoardDisplaySettings } from "../../lib/crmApi";

const DEFAULT_BOARD_COLOR_TOKENS: Record<string, string> = {
  "#dff5e9": "var(--color-board-aging-1)",
  "#fff0c7": "var(--color-board-aging-2)",
  "#ffe1e7": "var(--color-board-aging-3)",
  "#f5d0d6": "var(--color-board-aging-4)",
  "#eceefc": "var(--color-board-terminal-positive)",
};

const LEGACY_DARK_BOARD_COLORS = new Set([
  "#0f89a3",
  "#f5b915",
  "#d83b36",
  "#6b5fb5",
  "#2ca02c",
]);

type BoardColorRole =
  | "aging-1"
  | "aging-2"
  | "aging-3"
  | "aging-4"
  | "terminal-positive"
  | "terminal-negative";

const BOARD_ROLE_TOKENS: Record<BoardColorRole, string> = {
  "aging-1": "var(--color-board-aging-1)",
  "aging-2": "var(--color-board-aging-2)",
  "aging-3": "var(--color-board-aging-3)",
  "aging-4": "var(--color-board-aging-4)",
  "terminal-positive": "var(--color-board-terminal-positive)",
  "terminal-negative": "var(--color-board-terminal-negative)",
};

function boardColor(value: string | null | undefined, role: BoardColorRole): string | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  if (LEGACY_DARK_BOARD_COLORS.has(normalized)) return BOARD_ROLE_TOKENS[role];
  return DEFAULT_BOARD_COLOR_TOKENS[normalized] ?? value;
}

/** Pick the open-card aging colour for a given age (days). */
export function agingBackground(settings: BoardDisplaySettings | null | undefined, ageDays: number | null | undefined): string | undefined {
  if (!settings) return "var(--color-board-aging-1)";
  const days = Math.max(0, ageDays ?? 0);
  if (days < settings.aging_band1_max_days) return boardColor(settings.aging_band1_color, "aging-1");
  if (days < settings.aging_band2_max_days) return boardColor(settings.aging_band2_color, "aging-2");
  if (days < settings.aging_band3_max_days) return boardColor(settings.aging_band3_color, "aging-3");
  return boardColor(settings.aging_band4_color, "aging-4");
}

export function terminalPositiveBackground(value: string | null | undefined): string {
  return boardColor(value, "terminal-positive") ?? "var(--color-board-terminal-positive)";
}

export function terminalNegativeBackground(value: string | null | undefined): string {
  const mapped = boardColor(value, "terminal-negative");
  if (mapped === "var(--color-board-terminal-positive)") return "var(--color-board-terminal-negative)";
  return mapped ?? "var(--color-board-terminal-negative)";
}
